import '../page-list/PageList.css';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, useFilter } from '../helpers/FilterComponent';
import { formatInTimeZone } from 'date-fns-tz';
import {
    deleteScheduleDefinition,
    disableScheduleDefinition,
    enableScheduleDefinition,
    getScheduleDefinitions,
} from '../../services/notificationScheduleDefinitionService';
import { getMessage } from '../../services/clientService';
import { useAuthContext } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';
import styles from './NotificationScheduleList.module.scss';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const EMPTY_VALUE = '-';

const getRows = response => {
    if (Array.isArray(response)) return response;
    return response?.viewModels || response?.items || response?.data || [];
};

const getId = row => row.id ?? row.notificationScheduleDefinitionId;

const getEnabled = row =>
    row.status === 'Disabled' || row.status === 'disabled'
        ? false
        : row.statusName === 'Disabled' || row.statusName === 'disabled'
          ? false
          : row.status === 'Active' ||
              row.status === 'Completed' ||
              row.status === 1 ||
              row.statusName === 'Active' ||
              row.statusName === 'Completed'
            ? true
            : (row.isEnabled ??
              row.enabled ??
              (row.status === 'Active' || row.status === 1));

const getNextDate = row => {
    const candidate =
        row.nextScheduledDate ?? row.nextScheduledFor ?? row.scheduledFor;
    if (!candidate) return null;

    const date = parseUtcDate(candidate);
    return Number.isNaN(date.getTime()) || date.getTime() <= Date.now()
        ? null
        : date;
};

const getLastSentDate = row => {
    const candidate = row.lastSentAt ?? row.sentAt;
    if (!candidate) return null;

    const date = parseUtcDate(candidate);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getFrequencyLabel = row =>
    row.frequencyName ||
    row.notificationFrequencyName ||
    row.frequency ||
    EMPTY_VALUE;

const getPopulationGroups = row =>
    row.populationGroupNames ||
    row.populationGroups ||
    row.notificationGroups ||
    [];

const getStatusLabel = row => {
    if (row.statusName) return row.statusName;
    if (typeof row.status === 'string') return row.status;

    if (!getEnabled(row)) return getText('msg_f4f4473df8cb');
    if (getNextDate(row)) return getText('msg_a733b809d2f1');
    if (getLastSentDate(row)) return getText('msg_1798b3ba42ee');

    return getText('msg_f4f4473df8cb');
};

const utcDateTimePattern = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const parseUtcDate = value => {
    if (!value) return null;

    if (value instanceof Date) return value;

    const input = String(value).trim();
    const normalizedInput = utcDateTimePattern.test(input)
        ? input
        : `${input}Z`;

    const parsedDate = new Date(normalizedInput);
    if (!Number.isNaN(parsedDate.getTime())) return parsedDate;

    const [datePart, timePart = '00:00:00'] = input.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours = 0, minutes = 0, secondsPart = '0'] = timePart.split(':');
    const [seconds = '0', fractional = '0'] = String(secondsPart).split('.');
    const milliseconds = Number(fractional.padEnd(3, '0').slice(0, 3));

    const fallbackDate = new Date(
        Date.UTC(
            year,
            month - 1,
            day,
            Number(hours),
            Number(minutes),
            Number(seconds),
            milliseconds,
        ),
    );

    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
};

const formatDateTime = inputDate => {
    if (!inputDate) return EMPTY_VALUE;

    const date = parseUtcDate(inputDate);
    if (!date) return EMPTY_VALUE;

    if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return formatInTimeZone(date, userTimeZone, 'MM-dd-yyyy h:mm a');
};

const formatNextScheduledDateTime = formatDateTime;

const getStatusClassName = row => {
    const status = getStatusLabel(row);

    if (status === getText('msg_a733b809d2f1')) return styles.activeBadge;
    if (status === getText('msg_1798b3ba42ee')) return styles.completedBadge;
    return styles.disabledBadge;
};

const NotificationScheduleList = () => {
    const { notificationId } = useParams();
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { hasSecurityAttribute } = useAuthContext();
    const tenantId = +window.localStorage.getItem('tenantId');

    const canEdit = hasSecurityAttribute([
        SecurityAttributes.NotificationFullAccess,
    ]);

    const [rows, setRows] = useState([]);
    const [notificationName, setNotificationName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [actionBusyId, setActionBusyId] = useState(null);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);

    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);

    const loadPage = useCallback(async () => {
        setIsLoading(true);
        try {
            const [scheduleResponse, notificationResponse] = await Promise.all([
                getScheduleDefinitions(tenantId, notificationId),
                getMessage(tenantId, notificationId),
            ]);

            const scheduleRows = getRows(scheduleResponse);
            setRows(scheduleRows);
            setBaseFilteredData(scheduleRows);
            setNotificationName(notificationResponse?.name || 'Notification');
        } catch (error) {
            console.error('Failed to load notification schedules', error);
            Toast.error('Failed to load notification schedules.');
        } finally {
            setIsLoading(false);
        }
    }, [tenantId, notificationId]);

    useEffect(() => {
        loadPage();
    }, [loadPage]);

    useEffect(() => {
        setBaseFilteredData(rows);
    }, [rows]);

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredRows = rows.filter(row =>
            Object.entries(filtersWithoutCurrent).every(([key, values]) => {
                if (!values?.length) return true;

                switch (key) {
                    case 'populationGroups':
                        return getPopulationGroups(row).some(group =>
                            values.includes(group),
                        );
                    case 'status':
                        return values.includes(getStatusLabel(row));
                    case 'frequency':
                        return values.includes(getFrequencyLabel(row));
                    case 'lastSent':
                        return values.includes(
                            formatDateTime(getLastSentDate(row)),
                        );
                    case 'nextScheduled':
                        return values.includes(
                            formatDateTime(getNextDate(row)),
                        );
                    case 'name':
                    default:
                        return values.includes(row.name);
                }
            }),
        );

        switch (column) {
            case 'name':
                return Array.from(new Set(filteredRows.map(row => row.name)))
                    .filter(Boolean)
                    .sort();

            case 'status':
                return Array.from(
                    new Set(filteredRows.map(row => getStatusLabel(row))),
                )
                    .filter(Boolean)
                    .sort();

            case 'populationGroups':
                return Array.from(
                    new Set(
                        filteredRows.flatMap(row => getPopulationGroups(row)),
                    ),
                )
                    .filter(Boolean)
                    .sort();

            case 'frequency':
                return Array.from(
                    new Set(filteredRows.map(row => getFrequencyLabel(row))),
                )
                    .filter(Boolean)
                    .sort();

            case 'lastSent':
                return Array.from(
                    new Set(
                        filteredRows.map(row =>
                            formatDateTime(getLastSentDate(row)),
                        ),
                    ),
                )
                    .filter(value => value && value !== EMPTY_VALUE)
                    .sort();

            case 'nextScheduled':
                return Array.from(
                    new Set(
                        filteredRows.map(row =>
                            formatDateTime(getNextDate(row)),
                        ),
                    ),
                )
                    .filter(value => value && value !== EMPTY_VALUE)
                    .sort();

            default:
                return [];
        }
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const clearFilters = () => {
        setSelections({});
        clearAllFilters();
    };

    const sortedRows = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;

            switch (sortConfig.key) {
                case 'status':
                    aValue = getStatusLabel(a).toLowerCase();
                    bValue = getStatusLabel(b).toLowerCase();
                    break;
                case 'populationGroups':
                    aValue = getPopulationGroups(a).join(', ').toLowerCase();
                    bValue = getPopulationGroups(b).join(', ').toLowerCase();
                    break;
                case 'frequency':
                    aValue = getFrequencyLabel(a).toLowerCase();
                    bValue = getFrequencyLabel(b).toLowerCase();
                    break;
                case 'lastSent':
                    aValue = getLastSentDate(a)?.getTime() || 0;
                    bValue = getLastSentDate(b)?.getTime() || 0;
                    break;
                case 'nextScheduled':
                    aValue = getNextDate(a)?.getTime() || 0;
                    bValue = getNextDate(b)?.getTime() || 0;
                    break;
                case 'name':
                default:
                    aValue = String(a.name || '').toLowerCase();
                    bValue = String(b.name || '').toLowerCase();
                    break;
            }

            const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;

            return sortConfig.direction === SortDirections.ASCENDING
                ? comparison
                : -comparison;
        });
    }, [filteredData, sortConfig]);

    const handleSort = key => {
        setSortConfig(prev => ({
            key,
            direction:
                prev.key === key && prev.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const renderSortArrow = key => (
        <span className={`mx-2 ${styles.sortArrowIcon}`}>
            <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                <path
                    transform={
                        sortConfig.key === key &&
                        sortConfig.direction === SortDirections.DESCENDING
                            ? 'rotate(180 5.25 3)'
                            : ''
                    }
                    d="M1 0.75L5.25 5.25L9.5 0.75"
                    stroke="#2F2B43"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );

    const handlerRowClicked = id => {
        setRowsClicked(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id],
        );
    };

    const deselectRows = selectedRows => {
        selectedRows.forEach(rowId => {
            const checkbox = document.getElementById(`schedule-${rowId}`);
            if (checkbox) checkbox.checked = false;
        });
        setRowsClicked([]);
    };

    const onToggle = async row => {
        const id = getId(row);
        if (actionBusyId !== null) return;

        const nextEnabled = !getEnabled(row);

        setActionBusyId(id);
        try {
            if (getEnabled(row)) {
                await disableScheduleDefinition(tenantId, notificationId, id);
                Toast.success(`'${row.name}' was disabled.`);
            } else {
                await enableScheduleDefinition(tenantId, notificationId, id);
                Toast.success(`'${row.name}' was enabled.`);
            }

            const updateScheduleRow = rowItem =>
                getId(rowItem) === id
                    ? {
                          ...rowItem,
                          isEnabled: nextEnabled,
                          enabled: nextEnabled,
                          statusName: undefined,
                          status: undefined,
                      }
                    : rowItem;

            setRows(prev => prev.map(updateScheduleRow));
            setBaseFilteredData(prev => prev.map(updateScheduleRow));
        } catch (error) {
            console.error(
                'Failed to change notification schedule status',
                error,
            );
            Toast.error(`Failed to update '${row.name}'.`);
        } finally {
            setActionBusyId(null);
        }
    };

    const deleteSchedules = async () => {
        const selectedRows = [...rowsClicked];
        setIsLoading(true);

        try {
            for (const rowId of selectedRows) {
                const row = rows.find(item => getId(item) === rowId);
                if (!row) continue;

                try {
                    await deleteScheduleDefinition(
                        tenantId,
                        notificationId,
                        rowId,
                    );
                    Toast.success(`'${row.name}' was deleted.`);
                } catch (error) {
                    console.error(`Failed to delete '${row.name}'`, error);
                    Toast.error(`Failed to delete '${row.name}'.`);
                }
            }

            await loadPage();
        } finally {
            setIsLoading(false);
            deselectRows(selectedRows);
        }
    };

    const rowAction = action => {
        if (rowsClicked.length === 0) {
            Toast.error(
                'Please select one or more rows to perform this operation.',
            );
            return;
        }

        switch (action) {
            case 'delete':
                showModal({
                    type: ModalTypes.DELETE_CONFIRMATION,
                    title: 'Delete scheduled instance?',
                    text: 'This action will permanently delete the selected scheduled instance(s).',
                    onYes: deleteSchedules,
                    onNo: () => {},
                    noButtonText: 'Cancel',
                    yesButtonText: 'Delete',
                });
                break;
            default:
                break;
        }
    };

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex flex-column mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="float-start d-flex">
                                <p className="page-title card-title ms-4 me-5">
                                    {notificationName} Details
                                </p>
                            </div>

                            <div className="float-end d-flex align-items-center me-4">
                                <span
                                    type="button"
                                    onClick={() => navigate(ROUTES.MESSAGES)}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-x me-2" />
                                    Close
                                </span>
                            </div>
                        </div>

                        <br />
                        <br />

                        <div className={`${styles.toolbarRow} ms-4 me-4 mb-3`}>
                            <div />

                            <div className={styles.toolbarActions}>
                                <span
                                    type="button"
                                    onClick={clearFilters}
                                    className="ing-text-buttons"
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    Clear Filters
                                </span>

                                {canEdit && (
                                    <span
                                        type="button"
                                        onClick={() => rowAction('delete')}
                                        className="ing-text-buttons"
                                    >
                                        <i className="bi bi-trash3 me-2" />
                                        Delete
                                    </span>
                                )}

                                {canEdit && (
                                    <span
                                        onClick={() =>
                                            navigate(
                                                ROUTE_BUILDERS.notificationScheduleNew(
                                                    notificationId,
                                                ),
                                            )
                                        }
                                        type="button"
                                        className="button-base ing-button-primary-left align-middle"
                                    >
                                        <i className="bi bi-plus-lg me-1" />
                                        Add
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('name')}
                                    >
                                        <span className="table-column-header-text">
                                            Name {renderSortArrow('name')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('name');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('status')}
                                    >
                                        <span className="table-column-header-text">
                                            Status {renderSortArrow('status')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('status');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'status' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort('populationGroups')
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            Population Groups{' '}
                                            {renderSortArrow(
                                                'populationGroups',
                                            )}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('populationGroups');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'populationGroups' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('frequency')}
                                    >
                                        <span className="table-column-header-text">
                                            Frequency{' '}
                                            {renderSortArrow('frequency')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('frequency');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'frequency' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() => handleSort('lastSent')}
                                    >
                                        <span className="table-column-header-text">
                                            Last Sent{' '}
                                            {renderSortArrow('lastSent')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('lastSent');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'lastSent' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort('nextScheduled')
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            Next Scheduled{' '}
                                            {renderSortArrow('nextScheduled')}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('nextScheduled');
                                            }}
                                            className={`${styles.cursorPointer} pe-3`}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'nextScheduled' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>

                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            Enable
                                        </span>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedRows.map(row => {
                                    const id = getId(row);
                                    const enabled = getEnabled(row);
                                    const busy =
                                        actionBusyId !== null &&
                                        actionBusyId === id;
                                    const populationGroups =
                                        getPopulationGroups(row);

                                    return (
                                        <tr
                                            key={id}
                                            className={`${
                                                rowsClicked.includes(id)
                                                    ? 'clicked-class'
                                                    : ''
                                            }`}
                                        >
                                            <td className="text-center">
                                                <input
                                                    id={`schedule-${id}`}
                                                    onClick={() =>
                                                        handlerRowClicked(id)
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>

                                            <td
                                                className={`${styles.titleCell} ${styles.clickableNameCell}`}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() =>
                                                    navigate(
                                                        ROUTE_BUILDERS.notificationScheduleView(
                                                            notificationId,
                                                            getId(row),
                                                        ),
                                                    )
                                                }
                                                onKeyDown={event => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === ' '
                                                    ) {
                                                        navigate(
                                                            ROUTE_BUILDERS.notificationScheduleView(
                                                                notificationId,
                                                                getId(row),
                                                            ),
                                                        );
                                                    }
                                                }}
                                            >
                                                {row.name || 'Unnamed schedule'}
                                            </td>

                                            <td>
                                                <span
                                                    className={`${styles.statusBadge} ${getStatusClassName(row)}`}
                                                >
                                                    {getStatusLabel(row)}
                                                </span>
                                            </td>

                                            <td className="item-page-changed-by">
                                                <div
                                                    className={
                                                        styles.populationGroupWrap
                                                    }
                                                >
                                                    {populationGroups.length >
                                                    0 ? (
                                                        populationGroups.map(
                                                            (group, index) => (
                                                                <span
                                                                    key={`${id}-${group}-${index}`}
                                                                    className={
                                                                        styles.populationGroupBadge
                                                                    }
                                                                >
                                                                    {group}
                                                                </span>
                                                            ),
                                                        )
                                                    ) : (
                                                        <span
                                                            className={
                                                                styles.mutedValue
                                                            }
                                                        >
                                                            {EMPTY_VALUE}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="item-page-changed-by">
                                                {getFrequencyLabel(row)}
                                            </td>

                                            <td className="item-page-changed-by">
                                                {formatDateTime(
                                                    getLastSentDate(row),
                                                )}
                                            </td>

                                            <td className="item-page-changed-by">
                                                {formatNextScheduledDateTime(
                                                    getNextDate(row),
                                                )}
                                            </td>

                                            <td className="text-center">
                                                <div className={`form-switch`}>
                                                    <input
                                                        id={`schedule-status-${id}`}
                                                        onChange={() =>
                                                            onToggle(row)
                                                        }
                                                        aria-label={getText(
                                                            enabled
                                                                ? 'msg_193b6de0e3cb'
                                                                : 'msg_60b6091687f3',
                                                            {
                                                                scheduleName:
                                                                    row.name,
                                                            },
                                                        )}
                                                        disabled={busy}
                                                        className="form-check-input custom-switch-input"
                                                        type="checkbox"
                                                        checked={enabled}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {sortedRows.length <= 0 ? 'No schedules found' : ''}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotificationScheduleList;
